<template>
  <v-dialog v-model="dialogState" max-width="500px">
    <v-card>
      <template #title>
        <span class="text-primary">{{ title }}</span>
      </template>
      <template #text>
        <div v-if="loading">
          <div class="d-flex justify-center">
            <v-progress-circular :model-value="progress" color="primary" />
          </div>
          <br />
          <p>{{ t("components.downloadDialog.generating") }}</p>
        </div>
        <div v-else>
          <p>{{ t("components.downloadDialog.success") }}</p>
          <p>
            {{ t("components.downloadDialog.findReports") }}
            <a href="/reports" class="text-primary text-decoration-none">{{
              t("components.downloadDialog.reportsSection")
            }}</a>
          </p>
        </div>
      </template>
      <template #actions>
        <v-btn-nd class="ms-4 my-4" @click="dialogState = !dialogState">
          {{ t("form.close") }}
        </v-btn-nd>
      </template>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { AxiosError } from "axios";
import { onMounted, computed, inject, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import ReportApi from "@/api/merchant-service/report";
import { API } from "@/plugins/api";
import { useApplicationStore } from "@/store/application";

const { t } = useI18n();

const dialogState = defineModel<boolean>({ required: true });
const props = defineProps({ reportId: { type: String, default: undefined } });
const loading = ref(true);
const title = computed(() =>
  loading.value
    ? t("components.downloadDialog.downloadingTitle")
    : t("components.downloadDialog.downloadedTitle"),
);
const progress = ref(10);

const api = inject<API>("api");
const appStore = useApplicationStore();

onMounted(() => {
  if (dialogState.value && props.reportId) {
    progress.value = 10;
    downloadReport(props.reportId);
  }
});
watch(
  () => props.reportId,
  () => {
    if (dialogState.value && props.reportId) {
      progress.value = 10;
      downloadReport(props.reportId);
    }
  },
);

const downloadReport = async (reportId: string) => {
  loading.value = true;
  try {
    const result = await api!.report.presignedUrl(reportId);
    progress.value = 100;
    setTimeout(() => {
      loading.value = false;
      ReportApi.downloadReport(result.presignedUrl, result.fileName);
    }, 500);
  } catch (e) {
    if (e instanceof AxiosError) {
      const axiosError = e as AxiosError;
      if (axiosError.response?.status === 404) {
        loading.value = false;
        dialogState.value = false;
        appStore.notifyUser({
          message: t("components.downloadDialog.errorNotFound"),
          type: "error",
        });
        return;
      }
      const data = axiosError.response?.data as unknown as { state: string } | undefined;
      if (data?.state !== "READY") {
        progress.value += progress.value < 90 ? Math.random() * 4 + 1 : 0;
        setTimeout(() => downloadReport(reportId), Math.random() * 200 + 400);
      }
    }
  }
};
</script>
